import { ReactNode, useEffect, useMemo, useState } from 'react';
import jsPDF from 'jspdf';
import autoTable, { RowInput } from 'jspdf-autotable';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import {
  FiBarChart2,
  FiCalendar,
  FiCheckCircle,
  FiDollarSign,
  FiDownload,
  FiFileText,
  FiShield,
  FiTrendingDown,
  FiTrendingUp,
  FiUsers,
} from 'react-icons/fi';
import { AppLayout } from '../../components/AppLayout';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { Table } from '../../components/Table';
import { Kpi } from '../../components/Kpi';
import logoLizzi from '../../assets/logo.png';
import { buscarRelatorios } from '../../api/relatorios';
import { Relatorios } from '../../types';
import { formatarData, formatarMoeda, formatarQuantidade } from '../../utils/formatadores';

type ItemPorDia = { data: string; tipo: 'entrada' | 'saida'; total: number };
type Aba = 'entradas' | 'saidas' | 'ficha-tecnica' | 'equipe';

type DocumentoContexto = {
  titulo: string;
  subtitulo: string;
  descricao: string;
  arquivo: string;
  cor: [number, number, number];
  data: Relatorios;
  logoEmpresaDataUrl: string | null;
  logoLizziDataUrl: string | null;
};

const ABAS: Array<{ chave: Aba; label: string; icone: ReactNode }> = [
  { chave: 'entradas', label: 'Entradas', icone: <FiTrendingUp size={16} /> },
  { chave: 'saidas', label: 'Saidas', icone: <FiTrendingDown size={16} /> },
  { chave: 'ficha-tecnica', label: 'Ficha tecnica', icone: <FiFileText size={16} /> },
  { chave: 'equipe', label: 'Equipe', icone: <FiUsers size={16} /> },
];

function somaTipo(porDia: ItemPorDia[] | undefined, tipo: 'entrada' | 'saida') {
  return (porDia ?? []).filter((item) => item.tipo === tipo).reduce((soma, item) => soma + item.total, 0);
}

function serieDoTipo(porDia: ItemPorDia[] | undefined, tipo: 'entrada' | 'saida') {
  return (porDia ?? [])
    .filter((item) => item.tipo === tipo)
    .sort((a, b) => a.data.localeCompare(b.data))
    .map((item) => ({ data: item.data, dataFormatada: formatarData(item.data), total: item.total }));
}

function normalizarCnpj(valor?: string | null) {
  const digitos = String(valor || '').replace(/\D/g, '').slice(0, 14);
  if (digitos.length !== 14) return valor || '-';
  return digitos.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
}

function formatarDataHora(valor: Date | string) {
  return new Date(valor).toLocaleString('pt-BR');
}

function slugArquivo(valor: string) {
  return valor
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function carregarImagemComoDataUrl(src: string): Promise<string> {
  return fetch(src)
    .then((res) => res.blob())
    .then(
      (blob) =>
        new Promise<string>((resolve, reject) => {
          const leitor = new FileReader();
          leitor.onload = () => resolve(leitor.result as string);
          leitor.onerror = reject;
          leitor.readAsDataURL(blob);
        })
    );
}

function converterDataUrlParaPng(dataUrl: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const imagem = new Image();
    imagem.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = imagem.naturalWidth || imagem.width;
      canvas.height = imagem.naturalHeight || imagem.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('canvas'));
        return;
      }
      ctx.drawImage(imagem, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    imagem.onerror = reject;
    imagem.src = dataUrl;
  });
}

function desenharCabecalho(doc: jsPDF, contexto: DocumentoContexto) {
  const { data, titulo, subtitulo, descricao, cor, logoEmpresaDataUrl, logoLizziDataUrl } = contexto;
  const empresa = data.empresa;
  const largura = doc.internal.pageSize.getWidth();
  const altura = doc.internal.pageSize.getHeight();
  const margem = 14;

  doc.setFillColor(247, 250, 255);
  doc.roundedRect(margem, 10, largura - margem * 2, 44, 4, 4, 'F');
  doc.setDrawColor(225, 232, 245);
  doc.roundedRect(margem, 10, largura - margem * 2, 44, 4, 4);

  if (logoEmpresaDataUrl) {
    try {
      doc.addImage(logoEmpresaDataUrl, 'PNG', margem + 2, 15, 16, 16);
    } catch {
      // segue sem a logo da empresa
    }
  }

  if (logoLizziDataUrl) {
    try {
      doc.addImage(logoLizziDataUrl, 'PNG', largura - margem - 16, 15, 16, 16);
    } catch {
      // segue sem a logo do sistema
    }
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(cor[0], cor[1], cor[2]);
  doc.text(empresa.nome || 'Empresa', margem + 22, 18);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(84, 98, 121);
  doc.text(`CNPJ: ${normalizarCnpj(empresa.cnpj)}`, margem + 22, 24);
  doc.text(`Responsavel pela emissao: ${empresa.responsavel || '-'}`, margem + 22, 29);
  doc.text(`Emitido em: ${formatarDataHora(new Date())}`, margem + 22, 34);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(24, 32, 49);
  doc.text(titulo, margem, 64);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(85, 96, 112);
  doc.text(subtitulo, margem, 70);
  doc.setFontSize(9);
  doc.text(descricao, margem, 75, { maxWidth: largura - margem * 2 });

  doc.setDrawColor(225, 232, 245);
  doc.line(margem, altura - 16, largura - margem, altura - 16);
}

function desenharRodape(doc: jsPDF) {
  const largura = doc.internal.pageSize.getWidth();
  const altura = doc.internal.pageSize.getHeight();
  const margem = 14;
  const paginaAtual = doc.getCurrentPageInfo().pageNumber;
  const total = doc.getNumberOfPages();

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(110, 120, 136);
  doc.text('Relatorio gerencial para controle interno. Nao substitui livros fiscais ou contabeis oficiais.', margem, altura - 10);
  doc.text(`Pagina ${paginaAtual} de ${total}`, largura - margem, altura - 10, { align: 'right' });
}

function iniciarDocumento(contexto: DocumentoContexto) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  desenharCabecalho(doc, contexto);
  desenharRodape(doc);
  return doc;
}

function aplicarCabecalhoRodapeEmTodasPaginas(doc: jsPDF, contexto: DocumentoContexto) {
  const total = doc.getNumberOfPages();
  for (let pagina = 1; pagina <= total; pagina += 1) {
    doc.setPage(pagina);
    desenharCabecalho(doc, contexto);
    desenharRodape(doc);
  }
}

function tabelaDocumento(doc: jsPDF, contexto: DocumentoContexto, head: RowInput[], body: RowInput[]) {
  autoTable(doc, {
    startY: 84,
    margin: { top: 84, left: 14, right: 14, bottom: 22 },
    head,
    body,
    theme: 'grid',
    headStyles: {
      fillColor: contexto.cor,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      halign: 'left',
    },
    bodyStyles: {
      textColor: [32, 41, 57],
      lineColor: [225, 232, 245],
      lineWidth: 0.1,
      fontSize: 9,
      cellPadding: 2.6,
      valign: 'top',
    },
    alternateRowStyles: {
      fillColor: [248, 250, 253],
    },
    didDrawPage: () => {
      desenharCabecalho(doc, contexto);
      desenharRodape(doc);
    },
  });
}

async function criarContextoPdf(data: Relatorios, titulo: string, subtitulo: string, descricao: string, cor: [number, number, number]) {
  let logoEmpresaDataUrl: string | null = null;
  let logoLizziDataUrl: string | null = null;
  try {
    logoEmpresaDataUrl = data.empresa.logo_data_url ? await converterDataUrlParaPng(data.empresa.logo_data_url) : null;
  } catch {
    logoEmpresaDataUrl = null;
  }
  try {
    logoLizziDataUrl = await carregarImagemComoDataUrl(logoLizzi);
  } catch {
    logoLizziDataUrl = null;
  }

  return {
    titulo,
    subtitulo,
    descricao,
    cor,
    data,
    logoEmpresaDataUrl,
    logoLizziDataUrl,
    arquivo: slugArquivo(titulo),
  };
}

async function gerarPdfEntradas(data: Relatorios) {
  const contexto = await criarContextoPdf(
    data,
    'Relatorio de entradas',
    'Resumo financeiro de entradas registradas',
    'Documento com identificacao da empresa, resumo consolidado e movimento por data para conferencia gerencial.',
    [30, 158, 88]
  );
  const doc = iniciarDocumento(contexto);

  const head = [['Indicador', 'Valor', 'Observacao']];
  const body: RowInput[] = [
    ['Total de entradas', formatarMoeda(data.financeiro.total_entradas), 'Saldo acumulado no periodo consolidado'],
    [
      'Melhor dia de entrada',
      data.financeiro.melhor_dia_entrada ? formatarMoeda(data.financeiro.melhor_dia_entrada.total) : 'Sem dados',
      data.financeiro.melhor_dia_entrada ? formatarData(data.financeiro.melhor_dia_entrada.data) : '-',
    ],
    ['Entradas dos ultimos 30 dias', formatarMoeda(somaTipo(data.financeiro.por_dia, 'entrada')), 'Base da serie exibida no relatorio'],
  ];

  tabelaDocumento(doc, contexto, head, body);

  const serie = serieDoTipo(data.financeiro.por_dia, 'entrada');
  if (serie.length > 0) {
    autoTable(doc, {
      startY: (doc as any).lastAutoTable.finalY + 8,
      margin: { left: 14, right: 14, bottom: 22 },
      head: [['Data', 'Valor de entradas']],
      body: serie.map((item) => [item.dataFormatada, formatarMoeda(item.total)]),
      theme: 'striped',
      headStyles: { fillColor: contexto.cor, textColor: [255, 255, 255] },
      alternateRowStyles: { fillColor: [247, 251, 248] },
      didDrawPage: () => {
        desenharCabecalho(doc, contexto);
        desenharRodape(doc);
      },
    });
  }

  aplicarCabecalhoRodapeEmTodasPaginas(doc, contexto);
  doc.save(`${contexto.arquivo}-${new Date().toISOString().slice(0, 10)}.pdf`);
}

async function gerarPdfSaidas(data: Relatorios) {
  const contexto = await criarContextoPdf(
    data,
    'Relatorio de saidas',
    'Resumo financeiro de despesas e saldo operacional',
    'Documento gerencial para acompanhamento de saidas, saldo consolidado e comportamento do periodo recente.',
    [224, 41, 62]
  );
  const doc = iniciarDocumento(contexto);

  const body: RowInput[] = [
    ['Total de saidas', formatarMoeda(data.financeiro.total_saidas), 'Saidas acumuladas no consolidado'],
    ['Saldo operacional', formatarMoeda(data.financeiro.saldo), 'Entradas menos saidas'],
    ['Saidas dos ultimos 30 dias', formatarMoeda(somaTipo(data.financeiro.por_dia, 'saida')), 'Base da serie exibida abaixo'],
  ];

  tabelaDocumento(doc, contexto, [['Indicador', 'Valor', 'Observacao']], body);

  const serie = serieDoTipo(data.financeiro.por_dia, 'saida');
  if (serie.length > 0) {
    autoTable(doc, {
      startY: (doc as any).lastAutoTable.finalY + 8,
      margin: { left: 14, right: 14, bottom: 22 },
      head: [['Data', 'Valor de saidas']],
      body: serie.map((item) => [item.dataFormatada, formatarMoeda(item.total)]),
      theme: 'striped',
      headStyles: { fillColor: contexto.cor, textColor: [255, 255, 255] },
      alternateRowStyles: { fillColor: [254, 247, 248] },
      didDrawPage: () => {
        desenharCabecalho(doc, contexto);
        desenharRodape(doc);
      },
    });
  }

  aplicarCabecalhoRodapeEmTodasPaginas(doc, contexto);
  doc.save(`${contexto.arquivo}-${new Date().toISOString().slice(0, 10)}.pdf`);
}

async function gerarPdfFichaTecnica(data: Relatorios) {
  const contexto = await criarContextoPdf(
    data,
    'Relatorio de ficha tecnica',
    'Composicao, CMV e margem dos produtos',
    'Documento gerencial para conferencia de estrutura de custo e rentabilidade por produto cadastrado.',
    [30, 86, 232]
  );
  const doc = iniciarDocumento(contexto);

  tabelaDocumento(
    doc,
    contexto,
    [['Produto', 'Ficha tecnica', 'Preco', 'CMV', 'Margem']],
    data.produtos.map((produto) => [
      produto.nome,
      !produto.ficha_tecnica || produto.ficha_tecnica.length === 0
        ? '-'
        : produto.ficha_tecnica.map((item) => `${item.nome} (${formatarQuantidade(item.quantidade)} ${item.unidade_medida})`).join(', '),
      formatarMoeda(produto.preco_venda),
      formatarMoeda(produto.cmv),
      `${formatarMoeda(produto.margem_lucro)}${produto.margem_percentual !== null ? ` (${produto.margem_percentual}%)` : ''}`,
    ])
  );

  aplicarCabecalhoRodapeEmTodasPaginas(doc, contexto);
  doc.save(`${contexto.arquivo}-${new Date().toISOString().slice(0, 10)}.pdf`);
}

async function gerarPdfEquipe(data: Relatorios) {
  const contexto = await criarContextoPdf(
    data,
    'Relatorio de equipe',
    'Resumo de custo da equipe cadastrada',
    'Documento gerencial para controle de custo mensal do quadro ativo e comparacao entre contratos.',
    [30, 86, 232]
  );
  const doc = iniciarDocumento(contexto);

  const body: RowInput[] = [
    ['Custo total mensal', formatarMoeda(data.equipe.total_custo), 'Consolidado da equipe ativa'],
    ['Quantidade de funcionarios', String(data.equipe.funcionarios.length), 'Quadro considerado no relatorio'],
    [
      'Custo medio por funcionario',
      formatarMoeda(data.equipe.funcionarios.length ? data.equipe.total_custo / data.equipe.funcionarios.length : 0),
      'Media simples com base no custo total',
    ],
  ];

  tabelaDocumento(doc, contexto, [['Indicador', 'Valor', 'Observacao']], body);

  autoTable(doc, {
    startY: (doc as any).lastAutoTable.finalY + 8,
    margin: { left: 14, right: 14, bottom: 22 },
    head: [['Nome', 'Contrato', 'Salario', 'Custo total']],
    body: data.equipe.funcionarios.map((funcionario) => [
      funcionario.nome,
      funcionario.tipo_contrato === 'clt' ? 'CLT' : 'Outro',
      formatarMoeda(funcionario.salario),
      formatarMoeda(funcionario.custo_total),
    ]),
    theme: 'striped',
    headStyles: { fillColor: contexto.cor, textColor: [255, 255, 255] },
    alternateRowStyles: { fillColor: [248, 250, 253] },
    didDrawPage: () => {
      desenharCabecalho(doc, contexto);
      desenharRodape(doc);
    },
  });

  aplicarCabecalhoRodapeEmTodasPaginas(doc, contexto);
  doc.save(`${contexto.arquivo}-${new Date().toISOString().slice(0, 10)}.pdf`);
}

const GERADORES_PDF: Record<Aba, (data: Relatorios) => Promise<void>> = {
  entradas: gerarPdfEntradas,
  saidas: gerarPdfSaidas,
  'ficha-tecnica': gerarPdfFichaTecnica,
  equipe: gerarPdfEquipe,
};

export function RelatoriosLista() {
  const [dados, setDados] = useState<Relatorios | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [aba, setAba] = useState<Aba>('entradas');
  const [gerandoPdf, setGerandoPdf] = useState(false);

  useEffect(() => {
    buscarRelatorios()
      .then(setDados)
      .finally(() => setCarregando(false));
  }, []);

  const entradasSerie = useMemo(() => serieDoTipo(dados?.financeiro.por_dia, 'entrada'), [dados]);
  const saidasSerie = useMemo(() => serieDoTipo(dados?.financeiro.por_dia, 'saida'), [dados]);
  const produtosOrdenados = useMemo(
    () => (dados ? [...dados.produtos].sort((a, b) => Number(b.margem_lucro) - Number(a.margem_lucro)).slice(0, 6) : []),
    [dados]
  );
  const funcionariosOrdenados = useMemo(
    () => (dados ? [...dados.equipe.funcionarios].sort((a, b) => Number(b.custo_total) - Number(a.custo_total)).slice(0, 6) : []),
    [dados]
  );

  async function handleBaixarPdf() {
    if (!dados) return;
    setGerandoPdf(true);
    try {
      await GERADORES_PDF[aba](dados);
    } finally {
      setGerandoPdf(false);
    }
  }

  if (carregando || !dados) {
    return (
      <AppLayout>
        <p>Carregando relatorios...</p>
      </AppLayout>
    );
  }

  const cmvMedio = dados.produtos.length
    ? dados.produtos.reduce((soma, p) => soma + Number(p.cmv), 0) / dados.produtos.length
    : 0;
  const margensValidas = dados.produtos.filter((p) => p.margem_percentual !== null);
  const margemMedia = margensValidas.length
    ? margensValidas.reduce((soma, p) => soma + Number(p.margem_percentual), 0) / margensValidas.length
    : null;
  const custoMedioEquipe = dados.equipe.funcionarios.length ? dados.equipe.total_custo / dados.equipe.funcionarios.length : 0;

  return (
    <AppLayout>
      <Card
        style={{
          marginBottom: '22px',
          padding: '24px',
          background: 'linear-gradient(135deg, rgba(16,32,61,1), rgba(30,86,232,0.94))',
          color: '#fff',
          border: 'none',
          boxShadow: '0 20px 44px rgba(16, 32, 61, 0.18)',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '18px', alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <div
              style={{
                width: '64px',
                height: '64px',
                borderRadius: '18px',
                background: 'rgba(255,255,255,0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
              }}
            >
              {dados.empresa.logo_url ? (
                <img src={dados.empresa.logo_url} alt={dados.empresa.nome} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <FiBarChart2 size={26} />
              )}
            </div>

            <div>
              <p style={{ margin: 0, fontSize: '12px', letterSpacing: '0.1em', textTransform: 'uppercase', opacity: 0.72 }}>
                Central de relatorios
              </p>
              <h1 style={{ margin: '4px 0 6px', fontSize: '29px', lineHeight: 1.1 }}>{dados.empresa.nome}</h1>
              <p style={{ margin: 0, opacity: 0.82 }}>
                CNPJ: {normalizarCnpj(dados.empresa.cnpj)} · emissao gerencial em {formatarDataHora(new Date())}
              </p>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                padding: '10px 14px',
                borderRadius: '16px',
                background: 'rgba(255,255,255,0.1)',
              }}
            >
              <img src={logoLizzi} alt="Lizzi Food" style={{ width: '28px', height: '28px', objectFit: 'contain' }} />
              <div>
                <strong style={{ display: 'block', fontSize: '13px' }}>Lizzi Food</strong>
                <span style={{ fontSize: '12px', opacity: 0.78 }}>Documento emitido pelo sistema</span>
              </div>
            </div>

            <Button onClick={handleBaixarPdf} disabled={gerandoPdf} fullWidth={false}>
              <FiDownload size={16} style={{ marginRight: '6px', verticalAlign: '-2px' }} />
              {gerandoPdf ? 'Gerando PDF...' : 'Baixar PDF profissional'}
            </Button>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '18px' }}>
          <span style={{ padding: '8px 12px', borderRadius: '999px', background: 'rgba(255,255,255,0.12)', fontSize: '12px' }}>
            <FiShield size={14} style={{ marginRight: 6, verticalAlign: '-2px' }} />
            Identificacao da empresa e emissao
          </span>
          <span style={{ padding: '8px 12px', borderRadius: '999px', background: 'rgba(255,255,255,0.12)', fontSize: '12px' }}>
            <FiFileText size={14} style={{ marginRight: 6, verticalAlign: '-2px' }} />
            Rodape documental e paginacao
          </span>
          <span style={{ padding: '8px 12px', borderRadius: '999px', background: 'rgba(255,255,255,0.12)', fontSize: '12px' }}>
            <FiCheckCircle size={14} style={{ marginRight: 6, verticalAlign: '-2px' }} />
            Padrao gerencial profissional
          </span>
        </div>
      </Card>

      <div className="lizzi-abas">
        {ABAS.map((item) => (
          <button
            key={item.chave}
            className={`lizzi-aba ${aba === item.chave ? 'ativa' : ''}`}
            onClick={() => setAba(item.chave)}
            type="button"
          >
            {item.icone}
            {item.label}
          </button>
        ))}
      </div>

      {aba === 'entradas' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '20px' }}>
            <Kpi titulo="Total de entradas" valor={formatarMoeda(dados.financeiro.total_entradas)} tone="sucesso" icone={<FiTrendingUp size={20} />} />
            <Kpi
              titulo={dados.financeiro.melhor_dia_entrada ? `Melhor dia (${formatarData(dados.financeiro.melhor_dia_entrada.data)})` : 'Melhor dia de entrada'}
              valor={dados.financeiro.melhor_dia_entrada ? formatarMoeda(dados.financeiro.melhor_dia_entrada.total) : 'Sem dados'}
              tone="dourado"
              icone={<FiCalendar size={20} />}
            />
            <Kpi titulo="Entradas (ultimos 30 dias)" valor={formatarMoeda(somaTipo(dados.financeiro.por_dia, 'entrada'))} tone="sucesso" />
          </div>
          <Card style={{ marginBottom: '18px' }}>
            <h2 style={{ fontSize: '16px', marginBottom: '8px' }}>Entradas por dia</h2>
            <p style={{ marginTop: 0, marginBottom: '16px', fontSize: '13px', color: '#64748b' }}>
              Serie para acompanhamento das receitas no periodo recente.
            </p>
            {entradasSerie.length === 0 ? (
              <p style={{ fontSize: '14px', opacity: 0.7 }}>Nenhuma entrada registrada nos ultimos 30 dias.</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={entradasSerie}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--cor-borda)" />
                  <XAxis dataKey="dataFormatada" fontSize={12} />
                  <YAxis fontSize={12} />
                  <Tooltip formatter={(valor) => formatarMoeda(Number(valor))} />
                  <Bar dataKey="total" name="Entradas" fill="#1e9e58" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </>
      )}

      {aba === 'saidas' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '20px' }}>
            <Kpi titulo="Total de saidas" valor={formatarMoeda(dados.financeiro.total_saidas)} tone="erro" icone={<FiTrendingDown size={20} />} />
            <Kpi
              titulo="Saldo operacional"
              valor={formatarMoeda(dados.financeiro.saldo)}
              tone={dados.financeiro.saldo >= 0 ? 'sucesso' : 'erro'}
              icone={<FiDollarSign size={20} />}
            />
            <Kpi titulo="Saidas (ultimos 30 dias)" valor={formatarMoeda(somaTipo(dados.financeiro.por_dia, 'saida'))} tone="erro" />
          </div>
          <Card style={{ marginBottom: '18px' }}>
            <h2 style={{ fontSize: '16px', marginBottom: '8px' }}>Saidas por dia</h2>
            <p style={{ marginTop: 0, marginBottom: '16px', fontSize: '13px', color: '#64748b' }}>
              Serie de despesas para leitura rapida de concentracao e variacao.
            </p>
            {saidasSerie.length === 0 ? (
              <p style={{ fontSize: '14px', opacity: 0.7 }}>Nenhuma saida registrada nos ultimos 30 dias.</p>
            ) : (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={saidasSerie}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--cor-borda)" />
                  <XAxis dataKey="dataFormatada" fontSize={12} />
                  <YAxis fontSize={12} />
                  <Tooltip formatter={(valor) => formatarMoeda(Number(valor))} />
                  <Bar dataKey="total" name="Saidas" fill="#e0293e" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </>
      )}

      {aba === 'ficha-tecnica' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '20px' }}>
            <Kpi titulo="Produtos cadastrados" valor={String(dados.produtos.length)} icone={<FiFileText size={20} />} />
            <Kpi titulo="CMV medio" valor={formatarMoeda(cmvMedio)} tone="dourado" />
            <Kpi titulo="Margem media" valor={margemMedia !== null ? `${margemMedia.toFixed(1)}%` : 'Sem dados'} tone="sucesso" />
          </div>

          {produtosOrdenados.length > 0 && (
            <Card style={{ marginBottom: '20px' }}>
              <h2 style={{ fontSize: '16px', marginBottom: '8px' }}>Produtos mais lucrativos</h2>
              <p style={{ marginTop: 0, marginBottom: '16px', fontSize: '13px', color: '#64748b' }}>
                Ranking rapido para leitura de margem por item.
              </p>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={produtosOrdenados} layout="vertical" margin={{ left: 24 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--cor-borda)" />
                  <XAxis type="number" fontSize={12} />
                  <YAxis type="category" dataKey="nome" width={140} fontSize={12} />
                  <Tooltip formatter={(valor) => formatarMoeda(Number(valor))} />
                  <Bar dataKey="margem_lucro" name="Margem de lucro" fill="#1845ba" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}

          <Table>
            <thead>
              <tr>
                <th>Produto</th>
                <th>Ficha tecnica</th>
                <th>Preco de venda</th>
                <th>CMV</th>
                <th>Margem</th>
              </tr>
            </thead>
            <tbody>
              {dados.produtos.map((produto) => (
                <tr key={produto.id}>
                  <td>{produto.nome}</td>
                  <td style={{ fontSize: '13px' }}>
                    {!produto.ficha_tecnica || produto.ficha_tecnica.length === 0
                      ? '-'
                      : produto.ficha_tecnica.map((item) => `${item.nome} (${formatarQuantidade(item.quantidade)} ${item.unidade_medida})`).join(', ')}
                  </td>
                  <td>{formatarMoeda(produto.preco_venda)}</td>
                  <td>{formatarMoeda(produto.cmv)}</td>
                  <td>
                    {formatarMoeda(produto.margem_lucro)}
                    {produto.margem_percentual !== null && ` (${produto.margem_percentual}%)`}
                  </td>
                </tr>
              ))}
              {dados.produtos.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ padding: '16px', textAlign: 'center' }}>
                    Nenhum produto cadastrado ainda.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </>
      )}

      {aba === 'equipe' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px', marginBottom: '20px' }}>
            <Kpi titulo="Custo total mensal" valor={formatarMoeda(dados.equipe.total_custo)} tone="erro" icone={<FiDollarSign size={20} />} />
            <Kpi titulo="Funcionarios" valor={String(dados.equipe.funcionarios.length)} icone={<FiUsers size={20} />} />
            <Kpi titulo="Custo medio por funcionario" valor={formatarMoeda(custoMedioEquipe)} tone="dourado" />
          </div>

          {funcionariosOrdenados.length > 0 && (
            <Card style={{ marginBottom: '20px' }}>
              <h2 style={{ fontSize: '16px', marginBottom: '8px' }}>Custo por funcionario</h2>
              <p style={{ marginTop: 0, marginBottom: '16px', fontSize: '13px', color: '#64748b' }}>
                Ranking dos maiores custos para leitura gerencial do quadro ativo.
              </p>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={funcionariosOrdenados} layout="vertical" margin={{ left: 24 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--cor-borda)" />
                  <XAxis type="number" fontSize={12} />
                  <YAxis type="category" dataKey="nome" width={140} fontSize={12} />
                  <Tooltip formatter={(valor) => formatarMoeda(Number(valor))} />
                  <Bar dataKey="custo_total" name="Custo total" fill="#1845ba" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}

          <Table>
            <thead>
              <tr>
                <th>Nome</th>
                <th>Contrato</th>
                <th>Salario</th>
                <th>Custo total</th>
              </tr>
            </thead>
            <tbody>
              {dados.equipe.funcionarios.map((funcionario) => (
                <tr key={funcionario.id}>
                  <td>{funcionario.nome}</td>
                  <td>{funcionario.tipo_contrato === 'clt' ? 'CLT' : 'Outro'}</td>
                  <td>{formatarMoeda(funcionario.salario)}</td>
                  <td>{formatarMoeda(funcionario.custo_total)}</td>
                </tr>
              ))}
              {dados.equipe.funcionarios.length === 0 && (
                <tr>
                  <td colSpan={4} style={{ padding: '16px', textAlign: 'center' }}>
                    Nenhum funcionario cadastrado ainda.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </>
      )}
    </AppLayout>
  );
}
